<script setup lang="ts">
import dayjs from 'dayjs'
import { Mitteilung, MODE, type Attachment } from '@/models/Mitteilung.js'
import KpTextfield from '@/components/kp-textfield.vue'
import KpTextarea from '@/components/kp-textarea.vue'
import { computed, ref } from 'vue'
import { useAuthUser } from '@/helpers/globals'
import { buildUrl } from '@/helpers/api'
import { errToStr } from '@/helpers/tools'
import type { Gesuch } from '@/models/Gesuch'
import { useI18n } from 'vue-i18n'
import { useToast } from '@/composables/toast'
import { formRules } from '@/helpers/validators'

const { mitteilung, gesuch = null } = defineProps<{
    mitteilung: Mitteilung
    gesuch: Gesuch | null
}>()

const emit = defineEmits<{
    (e: 'mitteilungdestroyed'): void
    (e: 'change'): void
}>()

const formValid = ref(false)
const { t } = useI18n()
const toast = useToast()

const user = useAuthUser()

const created = computed(() => {
    return dayjs(mitteilung.created).format('DD.MM.YYYY HH:mm')
})

const fullyRead = computed(() => {
    return (
        mitteilung.gelesen_rollen &&
        mitteilung.gelesen_rollen.length === mitteilung.empfaenger_rollen?.length
    )
})

const ersteller_rolle = computed(() => {
    return getRolleBezeichnung(mitteilung.ersteller_rolle)
})

const empfaenger_rollen = computed(() => {
    if (mitteilung.empfaenger_rollen) {
        return mitteilung.empfaenger_rollen
            .map((item) => {
                return getRolleBezeichnung(item)
            })
            .join(', ')
    }
    return ''
})

const titletext = computed(() => {
    let res = created.value + ' | ' + ersteller_rolle.value + ' (' + mitteilung.ersteller
    if (mitteilung.ersteller_telefon) {
        res += ' ' + mitteilung.ersteller_telefon
    }
    res += ')'
    switch (mitteilung.mode) {
        case MODE.STANDARD:
            res += ' → ' + empfaenger_rollen.value
            break
        case MODE.BROADCAST:
            res += ' | ' + t('gesuch_comment')
            break
        case MODE.ENERGIEFACHSTELLE:
            res += ' → ' + t('bearbeiter')
            break
    }
    return res
})

async function save() {
    try {
        await mitteilung.save()
    } catch (res) {
        console.error(res)
        toast.add({ text: errToStr(res), color: 'error' })
    }
}

async function deleteMsg() {
    try {
        await mitteilung.destroy()
        emit('mitteilungdestroyed')
    } catch (res) {
        console.error(res)
        toast.add({ text: errToStr(res), color: 'error' })
    }
}

function getRolleBezeichnung(rolle: string | null) {
    if (rolle && rolle !== 'gesuchspruefer') {
        if (gesuch?.config?.config?.kontakte?.get(rolle)?.label) {
            return gesuch.config.config.kontakte.get(rolle).label
        }
    }
    return t('apollon_rolle_' + rolle)
}
function onAttachmentClick(att: Attachment) {
    const url = buildUrl('/mitteilung/downloadAttachment', {
        id: att.id,
    })
    window.open(url, 'apollon-dl')
}
function formatDate(value: string) {
    return dayjs(value).format('DD.MM.YYYY HH:mm')
}
</script>

<template>
    <v-expansion-panel
        :disabled="mitteilung.is_deleted || false"
        :class="{ 'msg-deleted': mitteilung.is_deleted }"
        @change="$emit('change')"
    >
        <v-expansion-panel-title>
            <div class="msg-header">
                <v-avatar>
                    <v-icon opacity=".5" v-if="mitteilung.is_deleted">fas fa-trash</v-icon>
                    <v-icon v-else-if="mitteilung.mode === MODE.BROADCAST">fas fa-bullhorn</v-icon>
                    <v-icon v-else-if="mitteilung.mode === MODE.ENERGIEFACHSTELLE"
                        >far fa-calendar-plus</v-icon
                    >
                    <v-icon v-else-if="fullyRead">far fa-envelope-open</v-icon>
                    <v-icon v-else>far fa-envelope</v-icon>
                </v-avatar>
                <div class="msg-title">
                    <div>
                        {{ titletext }}
                    </div>
                    <div v-if="mitteilung.is_deleted !== true">
                        <v-icon
                            v-if="mitteilung.attachments && mitteilung.attachments.length > 0"
                            size="small"
                        >
                            fas fa-paperclip
                        </v-icon>
                        {{ mitteilung.subject }}
                    </div>
                    <div v-if="mitteilung.is_deleted === true">
                        {{ t('deleted_at') }}: {{ mitteilung.deleted_at?.format('DD.MM.YYYY') }}
                    </div>
                </div>
            </div>
        </v-expansion-panel-title>
        <v-expansion-panel-text v-if="mitteilung.is_deleted !== true">
            <div
                v-if="
                    mitteilung.apollon_ersteller_id === user?.id &&
                    mitteilung.mode !== MODE.ENERGIEFACHSTELLE
                "
            >
                <v-form v-model="formValid" class="ml-4">
                    <kp-textfield
                        class="mb-4"
                        :model-value="mitteilung.subject"
                        :label="t('betreff')"
                        :rules="[formRules.required]"
                        @update:model-value="(value) => mitteilung.set('subject', value)"
                    ></kp-textfield>
                    <kp-textarea
                        :model-value="mitteilung.mitteilung"
                        :label="t('mitteilung')"
                        :rules="[formRules.required]"
                        @update:model-value="(value) => mitteilung.set('mitteilung', value)"
                    ></kp-textarea>
                </v-form>
            </div>
            <div v-else style="white-space: pre-line">
                {{ mitteilung.mitteilung }}
            </div>
            <v-card v-if="mitteilung.attachments && mitteilung.attachments.length" class="mt-5">
                <v-card-title>{{ t('anhaenge') }}</v-card-title>
                <v-list dense class="pt-0">
                    <template v-for="att in mitteilung.attachments" :key="att.id">
                        <v-list-item dense @click.stop="onAttachmentClick(att)">
                            <template #prepend>
                                <v-icon>fas fa-file-alt</v-icon>
                            </template>
                            <v-list-item-title>
                                {{ att.orig_filename }}
                            </v-list-item-title>
                            <v-list-item-subtitle>
                                {{ formatDate(att.created) }}
                            </v-list-item-subtitle>
                            <template #append>
                                <v-icon color="action white--text" size="small"
                                    >fas fa-download</v-icon
                                >
                            </template>
                        </v-list-item>
                    </template>
                </v-list>
            </v-card>
            <v-card-actions>
                <v-spacer />
                <v-btn
                    v-if="mitteilung.apollon_ersteller_id === user?.id"
                    icon="fa-solid fa-floppy-disk"
                    color="action white--text"
                    :disabled="!formValid || mitteilung.is_deleted || false"
                    @click="save"
                    size="small"
                >
                </v-btn>
                <v-btn
                    v-if="mitteilung.apollon_ersteller_id === user?.id"
                    icon="fas fa-trash"
                    color="stop white--text"
                    :disabled="!formValid || mitteilung.is_deleted || false"
                    @click="deleteMsg"
                    size="small"
                >
                </v-btn>
            </v-card-actions>
        </v-expansion-panel-text>
    </v-expansion-panel>
</template>

<style lang="sass" scoped>
.msg-header
    display: flex
    align-items: center
    .msg-title
        display: inline-block
</style>
